import { z } from 'zod';
export const EMAIL_TEMPLATES = [
    'verification_code',
    'welcome',
    'job_match',
    'password_changed',
    'document_rejected',
    'documents_approved',
    'document_reminder',
];
export const EMAIL_TEMPLATE_LABELS = {
    verification_code: '01 · Verification code',
    welcome: '02 · Welcome',
    job_match: '03 · Job match',
    password_changed: '04 · Password changed',
    document_rejected: '05 · Document rejected',
    documents_approved: '06 · Documents approved',
    document_reminder: '07 · Document reminder',
};
// Every language the client "main page" offers (apps/client/src/i18n) —
// email templates must cover the full set so a user never gets an email in
// a language other than the one they browse in. sr is Latin-script (matches
// the client's sr.json); mk is Cyrillic.
export const EMAIL_LOCALES = [
    'en',
    'ru',
    'ar',
    'de',
    'tr',
    'es',
    'fr',
    'pt',
    'hi',
    'pa',
    'sr',
    'hr',
    'bs',
    'sl',
    'mk',
    'sq',
    'hu',
];
export const EMAIL_LOCALE_LABELS = {
    en: 'English',
    ru: 'Русский',
    ar: 'العربية',
    de: 'Deutsch',
    tr: 'Türkçe',
    es: 'Español',
    fr: 'Français',
    pt: 'Português',
    hi: 'हिन्दी',
    pa: 'ਪੰਜਾਬੀ',
    sr: 'Srpski',
    hr: 'Hrvatski',
    bs: 'Bosanski',
    sl: 'Slovenščina',
    mk: 'Македонски',
    sq: 'Shqip',
    hu: 'Magyar',
};
export const EMAIL_LOCALE_DIRECTIONS = {
    en: 'ltr',
    ru: 'ltr',
    ar: 'rtl',
    de: 'ltr',
    tr: 'ltr',
    es: 'ltr',
    fr: 'ltr',
    pt: 'ltr',
    hi: 'ltr',
    pa: 'ltr',
    sr: 'ltr',
    hr: 'ltr',
    bs: 'ltr',
    sl: 'ltr',
    mk: 'ltr',
    sq: 'ltr',
    hu: 'ltr',
};
export const EMAIL_LOG_STATUSES = ['queued', 'sent', 'failed', 'skipped'];
// Variables passed to each template. Loose record shape — server enforces a
// sample-backed default for anything missing, so the admin debug UI can ship
// partial input.
export const emailVariablesSchema = z.record(z.string(), z.unknown());
export const emailPreviewInputSchema = z.object({
    template_key: z.enum(EMAIL_TEMPLATES),
    locale: z.enum(EMAIL_LOCALES),
    variables: emailVariablesSchema.optional(),
});
export const emailSendTestInputSchema = z.object({
    template_key: z.enum(EMAIL_TEMPLATES),
    locale: z.enum(EMAIL_LOCALES),
    recipient: z.string().trim().email().max(254),
    variables: emailVariablesSchema.optional(),
});
// Admin-tunable settings for the automatic "finish uploading your documents"
// reminder (template `document_reminder`). Stored as one app_settings row;
// the API sweep re-reads them every tick so edits apply without a restart.
//   enabled           — master switch for the sweep.
//   first_after_hours — wait after the docs became incomplete before the
//                       FIRST reminder (default 2 h).
//   interval_hours    — spacing between repeat reminders after the first
//                       (default 24 h).
//   max_sends         — reminders per user, lifetime. 0 also disables sending.
export const documentReminderSettingsSchema = z.object({
    enabled: z.boolean(),
    first_after_hours: z.number().int().min(1).max(720),
    interval_hours: z.number().int().min(1).max(720),
    max_sends: z.number().int().min(0).max(20),
});
// Ships disabled: on a first deploy every user whose documents stalled more
// than first_after_hours ago is immediately due, so an enabled default would
// mass-mail the whole backlog within one sweep tick. An admin turns the
// sweep on deliberately (Emails → Reminders). Disabled-by-default also means
// a malformed stored value falls back to "off", never to "mailing".
export const DOCUMENT_REMINDER_DEFAULTS = {
    enabled: false,
    first_after_hours: 2,
    interval_hours: 24,
    max_sends: 3,
};
//# sourceMappingURL=email.js.map